import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { finalize } from 'rxjs';
import { AuthService } from '../../core/services/auth/auth.service';
import { AdminReleasesService } from '../../core/services/release/admin-releases.service';
import { Release, ReleaseStatus } from '../../models/release';
import { Suite } from '../../models/suite';
import { TestCase } from '../../models/test-case';
import { TestPlan } from '../../models/test-plan';
import { ToastService } from '../../ui';
import { formatRange } from './release-dates';

/**
 * Admin → Configure release (drill-in from Manage releases). Shows everything the
 * release imported from Azure DevOps as a suite-grouped table of plans, each
 * expandable to its cases, with a Live switch per plan and per case. Replaces the
 * old opt-in plan-chip UX: the release starts fully populated and admins turn OFF
 * what it shouldn't include (isLive = false; nothing is deleted).
 */
@Component({
  selector: 'app-admin-release-config',
  standalone: true,
  imports: [RouterLink],
  templateUrl: './admin-release-config.component.html',
  styleUrl: './admin-release-config.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminReleaseConfigComponent implements OnInit {
  private adminReleases = inject(AdminReleasesService);
  private route = inject(ActivatedRoute);
  private toast = inject(ToastService);
  private auth = inject(AuthService);

  /** Placeholder rows rendered while the configuration loads. */
  readonly skeletonRows = [0, 1, 2, 3, 4];

  readonly release = signal<Release | null>(null);
  readonly isLoading = signal(false);
  readonly loadFailed = signal(false);
  readonly isImporting = signal(false);
  readonly expandedPlanIds = signal<Set<number>>(new Set());
  readonly casesByPlan = signal<Record<number, TestCase[]>>({});
  readonly casesLoadingIds = signal<Set<number>>(new Set());
  /** Plan/case ids with a Live toggle in flight (switch disabled meanwhile). */
  readonly pendingPlanIds = signal<Set<number>>(new Set());
  readonly pendingCaseIds = signal<Set<number>>(new Set());

  readonly suites = computed<Suite[]>(() => this.release()?.suites ?? []);
  readonly allPlans = computed<TestPlan[]>(() =>
    this.suites().flatMap((s) => s.testPlanSummaries ?? []),
  );

  private get userId(): number {
    return this.auth.userSig()?.userId ?? 1;
  }

  private get releaseId(): number {
    return Number(this.route.snapshot.paramMap.get('releaseId'));
  }

  ngOnInit(): void {
    this.load();
  }

  // ---- header ----

  statusClass(status: ReleaseStatus): string {
    return status.toLowerCase();
  }

  dateRange(r: Release): string {
    return formatRange(r.openOnUtc ?? r.createdOnUtc, r.signOffDueOnUtc ?? r.closedOnUtc);
  }

  livePlanCount(suite: Suite): number {
    return (suite.testPlanSummaries ?? []).filter((p) => p.isLive).length;
  }

  // ---- expand / cases ----

  isPlanExpanded(plan: TestPlan): boolean {
    return this.expandedPlanIds().has(plan.testPlanId);
  }

  casesFor(plan: TestPlan): TestCase[] {
    return this.casesByPlan()[plan.testPlanId] ?? [];
  }

  isCasesLoading(plan: TestPlan): boolean {
    return this.casesLoadingIds().has(plan.testPlanId);
  }

  togglePlanExpanded(plan: TestPlan): void {
    const set = new Set(this.expandedPlanIds());
    if (set.has(plan.testPlanId)) {
      set.delete(plan.testPlanId);
    } else {
      set.add(plan.testPlanId);
      this.loadCases(plan.testPlanId);
    }
    this.expandedPlanIds.set(set);
  }

  private loadCases(testPlanId: number, force = false): void {
    if (!force && this.casesByPlan()[testPlanId]) return; // already loaded
    this.casesLoadingIds.update((s) => new Set(s).add(testPlanId));

    this.adminReleases
      .getAllTestCases(testPlanId)
      .pipe(
        finalize(() =>
          this.casesLoadingIds.update((s) => {
            const next = new Set(s);
            next.delete(testPlanId);
            return next;
          }),
        ),
      )
      .subscribe({
        next: (cases) => this.casesByPlan.update((m) => ({ ...m, [testPlanId]: cases })),
        error: () => this.toast.error('Failed to load test cases.'),
      });
  }

  // ---- live toggles ----

  isPlanPending(plan: TestPlan): boolean {
    return this.pendingPlanIds().has(plan.testPlanId);
  }

  isCasePending(tc: TestCase): boolean {
    return this.pendingCaseIds().has(tc.testCaseId);
  }

  togglePlanLive(plan: TestPlan): void {
    const target = !plan.isLive;
    this.pendingPlanIds.update((s) => new Set(s).add(plan.testPlanId));

    this.adminReleases
      .setPlanLive(plan.testPlanId, target, this.userId)
      .pipe(
        finalize(() =>
          this.pendingPlanIds.update((s) => {
            const next = new Set(s);
            next.delete(plan.testPlanId);
            return next;
          }),
        ),
      )
      .subscribe({
        next: (updated) => this.patchPlan(plan.testPlanId, { isLive: updated.isLive }),
        error: () => this.toast.error('Failed to update the plan.'),
      });
  }

  toggleCaseLive(tc: TestCase): void {
    const target = !tc.isLive;
    this.pendingCaseIds.update((s) => new Set(s).add(tc.testCaseId));

    this.adminReleases
      .setCaseLive(tc.testCaseId, target, this.userId)
      .pipe(
        finalize(() =>
          this.pendingCaseIds.update((s) => {
            const next = new Set(s);
            next.delete(tc.testCaseId);
            return next;
          }),
        ),
      )
      .subscribe({
        next: (updated) => this.patchCase(tc.testPlanId, tc.testCaseId, { isLive: updated.isLive }),
        error: () => this.toast.error('Failed to update the test case.'),
      });
  }

  // ---- import ----

  importFromAzure(): void {
    this.isImporting.set(true);
    this.adminReleases
      .importFromAzure(this.releaseId, this.userId)
      .pipe(finalize(() => this.isImporting.set(false)))
      .subscribe({
        next: (config) => {
          this.release.set(config);
          // Cases may have been added by the import; drop the cache so expanded
          // plans re-fetch on next look.
          this.casesByPlan.set({});
          this.expandedPlanIds.set(new Set());
          this.toast.success('Import complete');
        },
        error: () => this.toast.error('Import failed. Check the Azure release id and try again.'),
      });
  }

  // ---- data ----

  private load(): void {
    this.isLoading.set(true);
    this.loadFailed.set(false);
    this.adminReleases
      .getConfiguration(this.releaseId)
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe({
        next: (config) => this.release.set(config),
        error: () => {
          this.loadFailed.set(true);
          this.toast.error('Failed to load the release configuration.');
        },
      });
  }

  private patchPlan(testPlanId: number, patch: Partial<TestPlan>): void {
    this.release.update((r) => {
      if (!r) return r;
      return {
        ...r,
        suites: r.suites.map((s) => ({
          ...s,
          testPlanSummaries: (s.testPlanSummaries ?? []).map((p) =>
            p.testPlanId === testPlanId ? { ...p, ...patch } : p,
          ),
        })),
      };
    });
  }

  private patchCase(testPlanId: number, testCaseId: number, patch: Partial<TestCase>): void {
    this.casesByPlan.update((m) => ({
      ...m,
      [testPlanId]: (m[testPlanId] ?? []).map((c) =>
        c.testCaseId === testCaseId ? { ...c, ...patch } : c,
      ),
    }));
  }
}
